import React from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineDot,
  TimelineContent,
} from "@mui/lab";
import {
  Typography,
  Skeleton,
  Box,
  useTheme,
  Divider,
  Paper,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { toast } from "react-toastify";
import { useAuth } from "hooks";

// Mapping of process names to their respective detail page URLs
const processUrlMap = {
  "Shipment Confirmed": (id) => `/app/crm/shipment/${id}/detail`,
  "Shipment Updated": (id) => `/app/crm/shipment/${id}/detail`,
  "Shipment Collected": (id) => `/app/crm/collection/${id}/detail`,
  "Shipment Consolidated": (id) => `/app/crm/consolidation/${id}/detail`,
  "COC Collection": (id) => `/app/crm/coc/${id}/detail`,
  "Shipment Loaded": (id) => `/app/crm/shipment-loading/${id}/detail`,
  "Shipment Unloaded": (id) => `/app/crm/shipment-unLoading/${id}/detail`,
  "Shipment Deconsolidated": (id) => `/app/crm/deconsolidation/${id}/detail`,
  "Shipment Stored": (id) => `/app/crm/warehousing/${id}/detail`,
  "Shipment Sent Out": (id) => `/app/crm/sendOut/${id}/detail`,
  "Shipment Failed": (id) => `/app/crm/sendOutCollection/${id}/detail`,
  "Shipment Cross Docked": (id) => `/app/crm/cross-docking/${id}/detail`,
  "Shipment Send Out Collected and marked as Delivery Failed": (id) =>
    `/app/crm/sendOutCollection/${id}/detail`,
};

// Helper function to validate and format date
const formatDate = (dateString) => {
  if (!dateString) {
    return "Date unavailable";
  }
  try {
    const date = parseISO(dateString); // Parse ISO 8601 string
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    return format(date, "dd MMM yyyy, hh:mm a"); // Format date
  } catch (error) {
    return "Date error";
  }
};

const ShipmentProcessTab = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver;
  const theme = useTheme();
  const navigate = useNavigate();
  const { ShipmentDetail, isLoading } = useShipmentDetailContext();
  const processList = ShipmentDetail?.shipmentProcess || [];
  const shipmentId = ShipmentDetail?._id; // Shipment ID from context

  // Handle click on a process
  const handleProcessClick = (item) => {
    const { process, relatedId } = item;
    const getUrl = processUrlMap[process];

    let idToUse = null;

    if (["Shipment Confirmed", "Shipment Updated"].includes(process)) {
      idToUse = shipmentId; // Use shipment ID for these
    } else {
      idToUse = relatedId; // Use related object ID for others
    }
    if (getUrl && idToUse) {
      const url = getUrl(idToUse);
      navigate(url);
    } else {
      console.warn(`No URL mapping or ID found for process: ${process}`);
      toast.warning("Detail page not available for this process.");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={3}>
        <Timeline sx={{ width: "100%" }}>
          {[...Array(3)].map((_, index) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="60%" />
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Box>
    );
  }

  if (!processList.length) {
    return (
      <Box mt={2}>
        <Typography variant="body1" color="text.secondary">
          No shipment processes available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Paper
        elevation={3}
        sx={{
          padding: 2,
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          width: "100%",
          boxShadow: theme.shadows[2],
        }}
      >
        <Timeline sx={{ pl: 0 }}>
          {[...processList]
            .sort((a, b) => {
              const dateA = parseISO(a.createdAt);
              const dateB = parseISO(b.createdAt);
              if (isNaN(dateA.getTime())) return 1; // Invalid dates go to the end
              if (isNaN(dateB.getTime())) return -1;
              return dateB - dateA; // Sort in descending order
            })
            .map((item, index, array) => {
              const isClickable = !canEdit;
              return (
                <TimelineItem
                  key={item._id || index}
                  sx={{
                    "&::before": { flex: 0 },
                    mt: index === 0 ? 1.5 : 0,
                    ...(isClickable && { cursor: "pointer" }),
                  }}
                  onClick={
                    isClickable ? () => handleProcessClick(item) : undefined
                  }
                >
                  <TimelineSeparator>
                    <TimelineDot
                      variant="filled"
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        width: 20,
                        height: 20,
                        mt: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {array.length - index}
                    </TimelineDot>
                    {index < array.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>

                  <TimelineContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="normal"
                        color={theme.palette.text.primary}
                      >
                        {item.process || "Unknown process"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic", whiteSpace: "nowrap" }}
                      >
                        {formatDate(item.createdAt)} by{" "}
                        {item.createdBy || "Unknown"}
                      </Typography>
                    </Box>

                    {item.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {item.description}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />
                  </TimelineContent>
                </TimelineItem>
              );
            })}
        </Timeline>
      </Paper>
    </Box>
  );
};

export default ShipmentProcessTab;
